import React, { useContext, useEffect } from "react";
import Modal from "common/Modal/Modal";
import {
  LanguageNameSpaces,
  OneClickFollowBotData,
  OneClickFollowSignalResponse,
  SymbolClientsData,
} from "@shared/types";
import { confirmWithCallback } from "utils/misc";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import styled, { useTheme } from "styled-components";
import Text from "common/Typography/Text";
import {
  CLOSED_TRADES_PATH,
  MOBILE_SEARCH,
  OPEN_TRADES_PATH,
} from "common/constants";
import { Link, useLocation } from "react-router-dom";
import { getTradeExtraDetailsRoute } from "utils/routes";
import { AnalyticsContext } from "analytics/AnalyticsProvider";
import Title from "common/Typography/Title";
import Button from "common/Buttons/Button";
import useMedia from "utils/hooks/useMedia";
import useHistoryWithQuery from "utils/hooks/useHistoryWithQuery";
import { typography } from "@shared/common/Themes/typography";
import {
  FollowSignalType,
  SignalFollowModalProps,
} from "@shared/features/signals/common/types";
import useFollowSignalModal from "features/signals/SignalFollow/useFollowSignalModal.tsx";
import SignalFollowBotsList from "features/signals/SignalFollow/SignalFollowBotsList";
import AnimatedCheckIcon from "common/Animations/AnimatedCheckIcon";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation.ts";
import { TRADE_EXTRA_DETAILS_TABS } from "@shared/common/constants.ts";

export const SignalFollowModal = ({
  closeModal,
  signal,
  followType,
}: SignalFollowModalProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.SIGNALS);
  const { t: tCommon } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const appTheme = useTheme();
  const { analytics } = useContext(AnalyticsContext);
  const [confirmModal, confirmModalContextHolder] = Modal.useModal();
  const location = useLocation();
  const {
    getDescription,
    successText,
    isFetching,
    signalFollowResponse,
    onCloseModal,
    title,
    onConfirm,
    isLoadingSignalFollow,
    analyticsArgs,
    quotaModal,
    isLoadingQuota,
  } = useFollowSignalModal();

  useEffect(() => {
    return () => {
      onCloseModal();
      closeModal();
    };
  }, [location.pathname]);

  const onListItemSelected = async (
    accountData?: SymbolClientsData,
    botData?: OneClickFollowBotData
  ) => {
    if (followType === undefined || !signal) return;
    const getConfirmMessage = () => {
      const objectName = botData?.name || accountData?.name;
      const objectText = t("follow.messages.object_text", {
        botOrAccount: botData
          ? t("follow.messages.bot")
          : t("follow.messages.account"),
        objectName,
      });
      const confirmMessage = {
        [FollowSignalType.FOLLOW]: t("follow.messages.follow_confirm", {
          objectText,
        }),
        [FollowSignalType.UPDATE]: t("follow.messages.update_confirm", {
          objectText,
        }),
        [FollowSignalType.CANCEL]: t("follow.messages.cancel_confirm", {
          objectText,
        }),
      };
      return confirmMessage[followType];
    };
    const isOkPressed = await confirmWithCallback(
      confirmModal,
      getConfirmMessage(),
      undefined,
      undefined,
      tCommon("labels.yes"),
      tCommon("labels.no")
    );

    if (isOkPressed) {
      await onConfirm(accountData, botData).catch(() => {});
      if (analyticsArgs) {
        analytics?.signalFollowupdatecloseModalSubmitAction(analyticsArgs);
      }
    }
  };

  if (followType === undefined || !signal || !successText) return null;

  const getContent = () => {
    const description = getDescription();
    return (
      <ContentWrapper>
        {!!description && (
          <Text
            $size={appTheme.typography.fontSizes.S}
            style={{ marginBottom: "8px", padding: "0 16px" }}
          >
            {description}
          </Text>
        )}
        <SignalFollowBotsList
          onListItemSelected={(botData) => {
            onListItemSelected(undefined, botData).catch(() => {});
          }}
          signal={signal}
        />
      </ContentWrapper>
    );
  };

  return (
    <>
      <Modal
        open={true}
        destroyOnHidden={true}
        onCancel={() => {
          closeModal();
          onCloseModal();
        }}
        centered={true}
        closable={true}
        styles={{
          body: {
            padding: signalFollowResponse ? 24 : isMobile ? 0 : 4,
            height: signalFollowResponse ? "auto" : 400,
          },
        }}
        title={title}
        footer={null}
        width={535}
      >
        <LoadingSpinnerOverlay
          isLoading={isFetching || isLoadingSignalFollow || isLoadingQuota}
          hideContentWhileLoading={isFetching}
          backgroundColor={appTheme.colors.cardBgColor}
        >
          <div style={{ height: "100%", display: "flex", flexFlow: "column" }}>
            {signalFollowResponse ? (
              <FollowSuccess
                followType={followType}
                title={successText}
                signalFollowResponse={signalFollowResponse!}
              />
            ) : (
              getContent()
            )}
          </div>
          {confirmModalContextHolder}
        </LoadingSpinnerOverlay>
      </Modal>
      {quotaModal}
    </>
  );
};

export default SignalFollowModal;

interface FollowSuccessProps {
  followType: FollowSignalType;
  title: string;
  signalFollowResponse: OneClickFollowSignalResponse;
}

const FollowSuccess = ({
  followType,
  title,
  signalFollowResponse,
}: FollowSuccessProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.SIGNALS);
  const location = useLocation();
  const { historyPush } = useHistoryWithQuery();

  const getTradeTableLink = () => {
    const linkName =
      followType === FollowSignalType.CANCEL
        ? t("trades.closed_trades")
        : t("trades.open_trades");
    const linkPath =
      followType === FollowSignalType.CANCEL
        ? CLOSED_TRADES_PATH
        : OPEN_TRADES_PATH;
    return (
      <Link to={linkPath} style={{ margin: "0 5px" }}>
        {linkName}
      </Link>
    );
  };

  const onClick = () => {
    const newTradeID = signalFollowResponse?.new_trade_id;
    const tradeExtraDetailsPath = newTradeID
      ? getTradeExtraDetailsRoute(
          location.pathname,
          newTradeID,
          TRADE_EXTRA_DETAILS_TABS.GENERAL
        )
      : undefined;
    if (tradeExtraDetailsPath) {
      historyPush(tradeExtraDetailsPath);
    }
  };

  const openOrClosedText = (
    followType === FollowSignalType.CANCEL
      ? t("status.closed")
      : t("status.open")
  ).toLowerCase();

  return (
    <div style={{ textAlign: "center" }}>
      <AnimatedCheckIcon size={70} />
      <Title level={2} style={{ margin: "8px 0 24px 0" }}>
        {title}
      </Title>
      <Text>{t("trades.see_trade_details")}</Text>
      <Button
        type="primary"
        onClick={onClick}
        style={{ margin: "12px 0 20px 0", width: 200 }}
      >
        {t("trades.trade_details")}
      </Button>
      <Text
        style={{
          fontSize: typography.fontSizes.S,
          fontStyle: "italic",
        }}
      >
        {t("trades.navigate")}
        {getTradeTableLink()}
        {t("trades.overview_trades", { openOrClosedText })}
      </Text>
    </div>
  );
};

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  padding-top: 16px;
`;
